import React, { useEffect, useState } from "react";
import styled from "styled-components";
import DefaultRoutesSVG from "../../../../SMARTCITY/assets/svg/components/DefaultRoutesSVG";
import * as Icon from "../../../../TEMPLATE/components/Map/smartiscity/Icons";
import { AmeaCategorySingularName, HandleBinIcon } from "../helpers";

import SmallCharts from "../../charts/SmallCharts/SmallCharts";
import { useTranslation } from "react-i18next";
import { MenuItem, Select } from "@material-ui/core";
import { useAllContexts } from "../../../hooks/useAllContexts";

const RightSidePanel = ({
  showRightSide,
  selectedItem,
  categoryId,
  selectedCategory,
}) => {
  const { t } = useTranslation();
  const InfoSection = ({ title, content }) => (
    <InfoSectionContainer>
      <SectionTitle>{title}</SectionTitle>
      <SectionText>{content}</SectionText>
      <SectionDevider />
    </InfoSectionContainer>
  );

  const { environment } = useAllContexts();
  const [localEnvironmentalData, setLocalEnvironmentalData] = useState([]);

  const [isDaily, setIsDaily] = useState(false);

  useEffect(() => {
    const tempData = isDaily
      ? environment.averageEnvironmentalData
      : environment.environmentalData;

    const filteredItem = tempData?.filter((item) => {
      return item.mysensor_id === selectedItem?.mysensor_id;
    })[0];
    setLocalEnvironmentalData(filteredItem?.details);
  }, [selectedItem, isDaily]);

  const InfoSectionHorizontal = ({
    title,
    content,
    isRightAligned = false,
  }) => (
    <InfoSectionContainer>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <SectionTitle>{title}</SectionTitle>
        <SectionText $isRightAligned={isRightAligned}>{content}</SectionText>
      </div>
      <SectionDevider />
    </InfoSectionContainer>
  );

  const InfrastructurePoint = ({ step, index }) => (
    <div>
      <PointContainer>
        <MarkerContainer>
          <MarkerNumber $isTwoDigits={index > 8}>{index + 1}</MarkerNumber>
          <img src={Icon.baseMarker} alt="Marker" />
        </MarkerContainer>
        <PointInfo>
          <p>{`${AmeaCategorySingularName(step.category)} (${step.title})`}</p>
          <p>{step.subTitle}</p>
        </PointInfo>
      </PointContainer>
      <SectionDevider />
    </div>
  );

  const renderMeteoData = (items) => {
    return items?.map((item, index) => (
      <MeteoItem key={`right_side_panel_content_${index}`}>
        <MeteoIcon
          src={`https://volos.smartiscity.gr/gallery/vars/Small/${item.sensorvar_icon}`}
        />
        <MeteoInfo>
          <SectionTitle>
            {categoryId === "1"
              ? t(`dataNames.${item.sensorvar_icon.slice(0, -4)}`)
              : item.sensorvar_description}
          </SectionTitle>
          <SectionText>{item.date_insert}</SectionText>
        </MeteoInfo>
        <MeteoValue>{`${parseFloat(item.measurement).toFixed(2)} ${
          item.sensorvar_unit
        }`}</MeteoValue>
      </MeteoItem>
    ));
  };

  const renderInfoBoxData = (item) => (
    <InfoBoxItem>
      <SectionTitle>{item[0]?.content}</SectionTitle>
      <SectionText>{item[0]?.value}</SectionText>
    </InfoBoxItem>
  );

  const renderCategoryIcon = () => {
    switch (categoryId) {
      case "1":
        return <i className="im im-icon-Globe" />;
      case "5":
        return <i className="im im-icon-Cloud-Sun" />;
      case "8":
        return <i className="im im-icon-Truck" />;
      case "9":
        return <i className="im im-icon-Recycling" />;
      case "10":
        return <i className="im im-icon-Gaugage" />;
      case "12":
        return <i className="im im-icon-On-Off-3" />;
      case "24":
        return <i className="im im-icon-Car-2" />;
      case "31":
        return <i className="im im-icon-Monitor-Vertical" />;
      case "122":
        return <DefaultRoutesSVG color="#fff" />;
      default:
        return null;
    }
  };

  const renderTitle = () => {
    if (categoryId === "122") {
      return `${selectedCategory === "infra" ? "Υποδομή" : "Διαδρομή"} ${
        selectedItem?.title
      }`;
    }
    if (categoryId === "8") {
      return `Κάδος ${selectedItem?.name}`;
    }
    if (categoryId === "9") {
      return `Κάδος ${selectedItem?.id}`;
    }
    if (categoryId === "10") {
      return `${selectedItem?.location}`;
    }
    if (categoryId === "24") {
      return `${selectedItem?.LabelOnStreet}`;
    }
    return categoryId === "1"
      ? t(`areaNames.${selectedItem?.ref_id}`)
      : selectedItem?.mysensor_name;
  };

  const renderContent = () => {
    switch (categoryId) {
      case "1":
        return renderMeteoData(localEnvironmentalData);
      case "5":
        return renderMeteoData(selectedItem?.details);
      case "8":
        return renderFleetData();
      case "9":
        return renderBinData();
      case "10":
        return renderHydroData();
      case "12":
        return renderHum();
      case "24":
        return renderParkingData();
      case "31":
        return (
          selectedItem?.metrics && renderInfoBoxData(selectedItem?.metrics)
        );
      case "122":
        return selectedCategory === "infra"
          ? renderInfrastructureInfo()
          : renderRouteInfo();
      default:
        return null;
    }
  };

  const renderBinData = () => (
    <>
      <MainTitle>{selectedItem?.location}</MainTitle>
      <InfoSectionHorizontal title="Κατάσταση" content={selectedItem?.type} />
      <InfoSectionHorizontal
        title="Κωδικός Αισθητήρα"
        content={selectedItem?.mote}
      />
      <InfoSectionHorizontal
        title="Τελευταία ενημέρωση"
        content={selectedItem?.date}
      />
      <InfoSectionHorizontal
        title="Συντεταγμένες κάδου"
        content={`lat: ${selectedItem?.lat}\nlng: ${selectedItem?.lng}`}
      />
      <InfoSectionHorizontal
        title="Ποσοστό πληρότητας κάδου"
        content={`${selectedItem?.value ? `${selectedItem?.value}%` : "NA"}`}
      />
      <InfoSectionHorizontal
        title="Τάση μπαταρίας"
        content={selectedItem?.voltage}
      />
      <InfoSectionHorizontal
        title="Θερμοκρασία"
        content={`${selectedItem?.temp}&deg;C`}
      />
      <div style={{ margin: "0 auto", position: "relative" }}>
        {HandleBinIcon(parseInt(selectedItem?.value), 140, 141)}
        <p
          style={{
            position: "absolute",
            top: "44px",
            left: "42px",
            fontWeight: "600",
            fontSize: "30px",
            color: selectedItem?.value ? "#000" : "#0000004f",
          }}
        >
          {selectedItem?.value ?? "N/A"}
          {selectedItem?.value ? "%" : ""}
        </p>
      </div>
      <div style={{ width: "350px", margin: "0 auto" }}>
        <SmallCharts binId={selectedItem?.id} />
      </div>
    </>
  );

  const renderHydroData = () => (
    <>
      <InfoSectionHorizontal
        title="Τελευταία ενημέρωση"
        content={selectedItem?.date}
      />
      <InfoSectionHorizontal
        title="Κατανάλωση τελευταίας ώρας"
        content={`${selectedItem?.value} κ.μ.`}
      />

      <div style={{ width: "350px", margin: "0 auto" }}>
        <SmallCharts binId={selectedItem?.id} categoryId="10" />
      </div>
    </>
  );

  const handleDate = (value) => {
    const date = value.slice(0, 10).split("/");
    const time = value.slice(11, 16);
    return `${date[2]}/${date[1]}/${date[0]}\n ${time}`;
  };

  const renderParkingData = () => (
    <>
      <InfoSectionHorizontal title="Τύπος Θέσης" content={selectedItem?.Type} />
      <InfoSectionHorizontal
        title="Κατάσταση"
        content={selectedItem?.IsOccupied ? "Κατειλημμένη" : "Ελεύθερη"}
      />
      <InfoSectionHorizontal
        isRightAligned
        title="Κατειλημμένη από"
        content={handleDate(selectedItem?.OccupiedFrom)}
      />
    </>
  );

  const renderFleetData = () => (
    <>
      <InfoSectionHorizontal
        title="Ταχύτητα"
        content={`${selectedItem?.speed} Km/h`}
      />
      <InfoSectionHorizontal
        title="Υψόμετρο"
        content={`${selectedItem?.altitude} μέτρα`}
      />
      <InfoSectionHorizontal
        title="Γωνία"
        content={`${selectedItem?.angle} μοίρες`}
      />
    </>
  );

  const renderInfrastructureInfo = () => (
    <>
      <MainTitle>Πληροφορίες Υποδομής</MainTitle>
      <InfoSection
        title="Κατηγορία Υποδομής"
        content={selectedItem?.category}
      />
      <InfoSection title="Χρήση Υποδομής" content={selectedItem?.type} />
    </>
  );

  const renderRouteInfo = () => {
    if (selectedItem) {
      return (
        <>
          <MainTitle>Πληροφορίες Διαδρομής</MainTitle>
          <InfoSection
            title="Κατηγορία Διαδρομής"
            content={selectedItem?.category}
          />
          <InfoSection
            title="Σημείο έναρξης"
            content={selectedItem?.start?.address}
          />
          <InfoSection
            title="Σημείο προορισμού"
            content={selectedItem?.end?.address}
          />
          <InfoSection
            title="Αριθμός Σημείων"
            content={selectedItem?.infrastructures?.length}
          />
          <InfoSection
            title="Απόσταση Διαδρομής"
            content={`${selectedItem?.distance}m`}
          />
          <MainTitle>Σημεία Υποδομών</MainTitle>
          {selectedItem?.infrastructures?.map((step, index) => (
            <InfrastructurePoint key={index} step={step} index={index} />
          ))}
        </>
      );
    }
  };

  const renderHum = () => {
    return (
      <>
        <InfoSectionHorizontal
          title="Κατανάλωση"
          content={`${selectedItem?.details[0]?.measurement} KWH`}
        />
      </>
    );
  };

  return (
    <RightSideContainer $top={showRightSide.get ? "70px" : "-84vh"}>
      {selectedItem && (
        <div style={{ width: "100%" }}>
          <div
            style={{
              width: "-webkit-fill-available",
              position: "fixed",
              backgroundColor: "#686868",
              color: "#fff",
              zIndex: 10,
            }}
          >
            <Header>
              <CategoryIcon
                $large={
                  categoryId === "1" ||
                  categoryId === "5" ||
                  categoryId === "31"
                }
              >
                {renderCategoryIcon()}
              </CategoryIcon>
              <Title>{renderTitle()}</Title>
              <CloseIcon
                src={Icon.close}
                onClick={() => showRightSide.set(false)}
              />
            </Header>
            <HeaderDivider />
          </div>
          <Content $isPadded={categoryId === "5" || categoryId === "1"}>
            {categoryId === "1" && (
              <SelectWrapper
                value={isDaily ? "daily" : "hour"}
                onChange={(event) => {
                  setIsDaily(event.target.value === "daily");
                  // setSelectedSensor(event.target.value);
                  // fetchGraph(event.target.value);
                }}
                placeholder="Επιλογή Δεδομένων"
              >
                {["daily", "hour"]?.map((dataCategory) => {
                  return (
                    <MenuItem key={`data_${dataCategory}`} value={dataCategory}>
                      {t(
                        `environmental.${
                          dataCategory === "daily"
                            ? "average_day"
                            : "average_hour"
                        }`
                      )}
                    </MenuItem>
                  );
                })}
              </SelectWrapper>
            )}
            {renderContent()}
          </Content>
        </div>
      )}
    </RightSideContainer>
  );
};

export default RightSidePanel;

const RightSideContainer = styled.div`
  width: 380px;
  height: calc(100vh - 70px - 30px);
  position: absolute;
  top: ${(props) => props.$top};
  right: 0;
  background-color: #686868;
  transition: all 1.4s ease;
  overflow-x: hidden;
  overflow-y: scroll;
  overflow-y: scroll;
  z-index: 2;
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none; /* Internet Explorer and Edge */
  scrollbar-width: none; /* Firefox */
`;

const HeaderDivider = styled.hr`
  width: 90%;
  border-bottom: 2px solid;
  border-image-source: linear-gradient(
    to left,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.8) 35%,
    rgba(255, 255, 255, 0.8) 65%,
    rgba(255, 255, 255, 0) 100%
  );
  border-image-slice: 1;
`;

const SectionDevider = styled.hr`
  width: 90%;
  border-bottom: 1px solid #ffffff04;
  border-left: none;
  border-right: none;
  margin-block: 10px;
`;

const MainTitle = styled.p`
  font-weight: 500;
  font-size: 15px;
  line-height: 20px;
  text-align: center;
  color: #fff;
  margin-block: 20px;
`;

const InfoSectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SectionTitle = styled.p`
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  letter: 0.5%;
  color: #fff;
  padding-inline: 20px;
`;

const SectionText = styled.p`
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #fff;
  letter: 0.5%;
  padding: 0 20px;
  white-space: pre-line;
  text-align: ${({ $isRightAligned }) => ($isRightAligned ? "right" : "unset")};
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  gap: 27px;
  justify-content: space-between;
  align-items: center;
  padding: 20px 20px 6px;
`;

const CategoryIcon = styled.div`
  width: ${(props) => (props.$large ? "80px" : "50px")};
  height: 50px;
  background-color: #27aae1;
  border-radius: 12px;
  display: flex;
  justify-content: center;
  align-items: center;

  i {
    color: #fff;
    font-size: 24px;
    font-weight: 600;
  }
`;

const Title = styled.p`
  color: #fff;
  font-weight: 500;
  font-size: 20px;
  line-height: 25px;
  text-align: center;
`;

const CloseIcon = styled.img`
  cursor: pointer;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: ${(props) => (props.$isPadded ? "22px" : "unset")};
  padding-top: 120px;
`;

const MeteoItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const MeteoIcon = styled.img`
  width: 40px;
`;

const MeteoInfo = styled.div``;

const MeteoValue = styled.p`
  margin-left: auto;
  color: #fff;
  width: 70px;
  text-align: right;
`;

const InfoBoxItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const PointContainer = styled.div`
  padding: 4px 20px;
  height: fit-content;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 20px;
  position: relative;
`;

const MarkerContainer = styled.div`
  position: relative;
`;

const MarkerNumber = styled.p`
  font-size: 20px;
  color: white;
  position: absolute;
  top: 6px;
  left: ${(props) => (props.$isTwoDigits ? "30%" : "40%")};
  margin-bottom: 4px;
`;

const PointInfo = styled.div`
  color: white;
  padding-top: 10px;
`;

const SelectWrapper = styled(Select)`
  width: 100%;
  .MuiSelect-root,
  .MuiSelect-select,
  .MuiSelect-selectMenu,
  .MuiInputBase-input,
  .MuiInput-input {
    color: white;
  }

  .MuiSvgIcon-root,
  .MuiSelect-icon {
    fill: white;
  }
  border-width: 0 0 2px 0;
  border-style: solid;
  border-color: white;

  &:hover {
    background-color: #ffffff1f;
    transition: all 0.3s ease-in-out;
  }
`;
